/**
 * The Recharts adapter — THE ONLY MODULE IN THIS PACKAGE THAT IMPORTS A CHART
 * LIBRARY.
 *
 * Keep it that way. Everything upstream speaks {@link ChartSpec}, which is why
 * replacing Recharts means writing a sibling of this file and changing the one
 * line that provides it, rather than touching every chart in the product. If
 * you find yourself importing `recharts` anywhere else, the abstraction has
 * already leaked and the swap is no longer cheap.
 *
 * Recharts is a PEER dependency: a host that never provides this adapter never
 * installs a charting library at all.
 *
 * Two behaviours here are load-bearing and easy to undo by accident:
 *
 *  1. Animation is on for MOUNT ONLY, and off entirely for a reader who has
 *     asked for reduced motion. A re-measure part-way through an animation
 *     freezes the series half-drawn, and a dashboard tile gets re-measured
 *     whenever it is resized. See `use-chart-animation.ts` in the web app,
 *     which solved this once already.
 *  2. Nulls are passed through untouched, with `connectNulls` OFF. Recharts
 *     breaks the line at a null, which is exactly what a gap in the data
 *     means. Turning `connectNulls` on draws a straight confident line across
 *     a month nobody has numbers for.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { YAxisTickContentProps } from 'recharts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ChartPoint, ChartSpec } from '../chart-spec';

/** Entry animation length. Long enough to read as growth, short enough not to delay. */
const ANIMATION_MS = 550;

/**
 * Whether this reader has asked the system for less motion.
 *
 * Recharts animates on mount, and a chart drawing itself in is exactly the
 * kind of motion `prefers-reduced-motion` exists to suppress — for some
 * readers it is a vestibular trigger, not a preference. Honoured live rather
 * than read once, because the setting can change while the page is open.
 *
 * Falls back to "no preference" wherever `matchMedia` is missing, which keeps
 * the animation on in server rendering rather than silently dropping it.
 */
function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const query = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
      query?.addEventListener('change', onChange);
      return () => query?.removeEventListener('change', onChange);
    },
    () => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    // Server snapshot: no window to ask, so assume no preference.
    () => false,
  );
}

/**
 * Animate on MOUNT ONLY, then switch off.
 *
 * Not a simplification — required, and for two separate reasons.
 *
 * Recharts interpolates against the width it measured when the animation
 * STARTED. A `ResponsiveContainer` measures 0 on its first paint and reports a
 * real width one frame later, so an animation begun at that moment interpolates
 * from nothing to nothing: the series render as empty `<g>` elements and simply
 * never appear, while the axes around them draw perfectly. That is a chart with
 * correct scales and no data in it, which is the worst possible failure — it
 * looks like an empty result rather than a broken renderer. Observed in the
 * report builder, where every bar, line and area came back blank.
 *
 * The second reason is the one the dashboard already knew about: a re-measure
 * WHILE an animation runs freezes the series part-drawn, and on a dashboard
 * re-measures are routine (resizing a tile dispatches one). Going static after
 * the first pass also makes a resize or a regrouping update instantly, which is
 * what you want once the reader is interacting rather than arriving.
 *
 * This is `use-chart-animation.ts` from the web app's fixture dashboard, moved
 * behind the adapter so every chart in the product gets it rather than only the
 * eight components that happened to import the hook.
 */
function useMountAnimation(): boolean {
  const reduced = useReducedMotion();
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    // A little past the animation, so the final frame has certainly landed
    // before the series goes static.
    const timer = setTimeout(() => setSettled(true), ANIMATION_MS + 150);
    return () => clearTimeout(timer);
  }, []);

  return !reduced && !settled;
}

const SERIES_COUNT = 6;
/**
 * The host's palette, not one of ours.
 *
 * Reading CSS custom properties rather than hard-coding hexes is what lets a
 * host re-theme charts without forking this file — and it is the same
 * `--rk-series-*` contract the hand-rolled SVG charts used, so existing themes
 * keep working across the swap.
 */
const seriesColor = (i: number) => `var(--rk-series-${(i % SERIES_COUNT) + 1})`;

/**
 * A series colour for a FILLED SHAPE — a bar or a slice — softened toward the
 * surface it sits on.
 *
 * A line is two pixels wide and wants the colour at full strength to be seen
 * at all. A bar is a slab: nine of them at full strength are most of the ink on
 * the page, and the eye reads the block rather than the lengths. Mixing toward
 * `--rk-surface` rather than lowering opacity keeps the fill solid, so the grid
 * does not show through it, and it goes the right way in both themes — lighter
 * on a light card, deeper on a dark one — the same reasoning the highlight
 * tones in `styles.css` follow.
 *
 * The hue is untouched, so a bar and the legend swatch beside it are still
 * recognisably one series.
 *
 * HOW MUCH is a theme decision, so it is a token — `--rk-fill-strength`, see
 * `styles.css` — rather than a number here. The same percentage cannot serve
 * both themes: on a light card it lifts a colour, on a dark one it sinks it, so
 * a strength that lightens light mode makes dark mode muddy.
 */
const seriesFill = (i: number) =>
  `color-mix(in oklab, ${seriesColor(i)} var(--rk-fill-strength, 55%), var(--rk-surface, #fff))`;

/**
 * The thickest a bar may be, in px.
 *
 * Recharts divides the whole band among the bars in it, so a chart of nine
 * people in a tall panel drew nine slabs 80px thick — heavier than anything
 * else on the page, and no easier to compare than a bar a third of that.
 */
const BAR_MAX_PX = 28;

/** Recharts wants row objects; a ChartSpec is column-oriented. Transpose once. */
function toRows(spec: ChartSpec): Record<string, string | number | null>[] {
  return spec.categories.map((category, ci) => {
    const row: Record<string, string | number | null> = { __label: category.label };
    spec.series.forEach((s, si) => {
      // Keyed by INDEX, not by the series key. Series labels and keys are data
      // — they can contain dots, spaces, or collide with `__label` — and
      // Recharts treats a dataKey as a path expression. `s0`/`s1` cannot.
      row[`s${si}`] = s.points[ci] ?? null;
    });
    return row;
  });
}

// `--rk-ink-soft`, not `--rk-muted`: the latter is declared nowhere and every
// tick painted with it fell through to `currentColor`. Recharts' own ticks were
// rescued by `.rk-chart .recharts-text` in the stylesheet; a hand-rolled tick
// has no such class, so the attribute has to be right on its own.
const AXIS = { fontSize: 11, fill: 'var(--rk-ink-soft, currentColor)' } as const;
const GRID = 'var(--rk-border, rgba(0,0,0,0.08))';

/**
 * Average advance of one character at the 11px axis face.
 *
 * Estimated rather than measured because both callers need a number BEFORE
 * anything is in the document: `ResponsiveContainer` wants the axis width up
 * front, and the tick has to decide how much of its label fits while it is
 * being built.
 */
const CHAR_PX = 6.2;

/** The widest and narrowest the category gutter may be, in px. */
const GUTTER_MAX = 200;
const GUTTER_MIN = 72;

/**
 * How many category rows can carry a legible label.
 *
 * A tick is 11px of text needing ~14px of row to clear its neighbours, and
 * `.rk-chart` is 320px less roughly 30px of value axis. Twenty rows is what
 * that divides into.
 */
const LABELLED_ROWS_MAX = 20;

/**
 * ONE LINE PER CATEGORY, always — truncated rather than wrapped.
 *
 * Recharts' default tick is a `<Text>` that WORD-WRAPS to the axis width, and
 * a wrapped label is two lines tall inside a row that is one line high. With
 * fifteen bars in a tile the rows are ~19px apart while a wrapped label needs
 * 24px, so the second line of one label lands on the first line of the next —
 * which is how rotating the chart to stop labels being dropped produced a
 * column of labels sitting on top of each other instead.
 *
 * The full text is not lost: it is on the tick's own `<title>`, and the
 * tooltip names the category too.
 */
function categoryTick(longest: number) {
  // TAKEN FROM THE LABEL, NOT BACK OUT OF THE GUTTER. Recovering it as
  // `(gutter - 12) / CHAR_PX` inverted a `Math.round`, and rounding down lost a
  // character — so at eleven-, twelve-, sixteen-character label lengths the
  // longest label was ellipsised inside a gutter widened to fit it exactly.
  const fits = Math.max(4, Math.min(longest, Math.floor((GUTTER_MAX - 12) / CHAR_PX)));
  // Recharts types the tick's coordinates as `string | number`, so they are
  // taken as given and handed straight back to SVG, which accepts both.
  return ({ x, y, payload }: YAxisTickContentProps) => {
    const label = String(payload?.value ?? '');
    const shown = label.length > fits ? middleTruncate(label, fits) : label;
    return (
      // `recharts-text` so the host's stylesheet reaches this tick the same way
      // it reaches every tick Recharts draws itself.
      <text
        className="recharts-text"
        x={x}
        y={y}
        dy={4}
        textAnchor="end"
        fontSize={AXIS.fontSize}
        fill={AXIS.fill}
      >
        <title>{label}</title>
        {shown}
      </text>
    );
  };
}

/**
 * Drop characters from the MIDDLE, not the end.
 *
 * These labels are `Customer · Role`, and the role is both the shorter half and
 * the one that distinguishes two rows from each other — cutting the tail turns
 * "Acme Robotics-Test · Bookkeeper" and "Acme Robotics-Test · Controller" into
 * the same string. Taking it out of the middle keeps the beginning that sorts
 * them and the ending that tells them apart.
 *
 * The full label is on the tick's `<title>` either way.
 */
function middleTruncate(label: string, fits: number): string {
  // One character goes to the ellipsis, so the halves share what is left. The
  // tail gets the odd character: it is the more informative end.
  const head = Math.floor((fits - 1) / 2);
  const tail = fits - 1 - head;
  return `${label.slice(0, head).trimEnd()}…${label.slice(label.length - tail).trimStart()}`;
}

/** Rounded data-end, square baseline. Tuples, because that is the prop's type. */
const RADIUS_UPRIGHT: [number, number, number, number] = [4, 4, 0, 0];
const RADIUS_SIDEWAYS: [number, number, number, number] = [0, 4, 4, 0];

/**
 * How much room the category labels need down the side of a sideways bar.
 *
 * Estimated from the longest label rather than measured: the labels are not in
 * the document when this runs, and `ResponsiveContainer` needs the width as a
 * number before it lays anything out. 6.2px per character is the average
 * advance of the 11px UI face, and the axis adds its own tick padding.
 *
 * The CAP is the part that matters. Without one, a single unusually long
 * category ("Acme Robotics-Test · Senior Controller") would take more than
 * half a tile's width and leave the bars a stub — so past `GUTTER_MAX` the
 * labels truncate instead, and the reader still has the tooltip. The FLOOR
 * keeps a chart of short names from crowding its own axis line.
 *
 * Takes the SAME length `categoryTick` does, so the width and the truncation
 * cannot disagree about what fits.
 */
function categoryGutter(longest: number): number {
  return Math.min(GUTTER_MAX, Math.max(GUTTER_MIN, Math.round(longest * CHAR_PX) + 12));
}

/** The longest category label, in characters. */
function longestLabel(rows: readonly Record<string, string | number | null>[]): number {
  return rows.reduce((max, row) => {
    const label = typeof row.__label === 'string' ? row.__label.length : 0;
    return label > max ? label : max;
  }, 0);
}

function tooltipFormatter(spec: ChartSpec) {
  return (value: unknown, name: unknown) => {
    const n = typeof value === 'number' ? value : null;
    const index = typeof name === 'string' ? Number(name.slice(1)) : Number.NaN;
    const series = spec.series[index];
    // The SERIES' format first: with two measures on the chart, each reads the
    // way its own table cell does.
    const format = series?.format ?? spec.format;
    return [format(n), series?.label ?? spec.measureLabel] as [string, string];
  };
}

/**
 * The hover on a sparkline: the period, and its value under it.
 *
 * DRAWN ABOVE THE LINE, into the metric's own box. A sparkline is a strip a
 * few dozen pixels tall at the bottom of a tile that clips, so a tooltip at
 * the pointer — the full chart's — is cut off before it can be read. Pinned
 * above, it sits over the figure's caption for as long as the pointer is
 * there, and stays inside the strip's width. Two short lines rather than one
 * long one, because a narrow tile is narrower than "Sep 2026 · to date 43,407".
 *
 * Read off the ROW, not recharts' `label`: a sparkline has no category axis,
 * so the label recharts would pass is the point's index.
 */
function sparkTooltip(spec: ChartSpec) {
  return (
    <Tooltip
      isAnimationActive={false}
      cursor={{ stroke: GRID }}
      position={{ y: -40 }}
      allowEscapeViewBox={{ x: false, y: true }}
      wrapperStyle={{ zIndex: 1, pointerEvents: 'none' }}
      content={({ active, payload }) => {
        const entry = active ? payload?.[0] : undefined;
        if (!entry) return null;
        const row = entry.payload as Record<string, unknown> | undefined;
        const value = typeof entry.value === 'number' ? entry.value : null;
        const series = spec.series[Number(String(entry.dataKey ?? '').slice(1))];
        return (
          <div
            className="rk-spark-tip"
            // The MUTED surface, bordered and faintly lifted: on the tile's
            // own white it otherwise read as part of the text it sits over.
            style={{
              background: 'var(--rk-surface-muted, #f4f4f5)',
              border: '1px solid var(--rk-border, #e4e4e7)',
              boxShadow: '0 1px 3px rgb(0 0 0 / 0.08)',
              borderRadius: 6,
              padding: '2px 6px',
              fontSize: 11,
              lineHeight: '15px',
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ color: 'var(--rk-ink-soft, #64748b)' }}>
              {typeof row?.__label === 'string' ? row.__label : ''}
            </div>
            <strong>{(series?.format ?? spec.format)(value)}</strong>
          </div>
        );
      }}
    />
  );
}

/**
 * A click on ONE band of ONE series — bars only.
 *
 * Recharts hands a `Bar` both coordinates, so this is the mark that can report
 * a complete {@link ChartPoint}.
 */
function barClick(spec: ChartSpec, seriesIndex: number) {
  if (!spec.onPointClick) return undefined;
  const fire = spec.onPointClick;
  return (_: unknown, categoryIndex: number) => {
    fire({ category: categoryIndex, series: seriesIndex } satisfies ChartPoint);
  };
}

/**
 * A click anywhere in a line or area plot.
 *
 * Attached to the CHART, not to each series, because Recharts' curve click
 * handler receives only the event — there is no per-point callback to hang a
 * series on. Recharts does resolve the x position for us and reports it as
 * `activeTooltipIndex`, which is the category.
 *
 * The series is deliberately left undefined: with several lines stacked
 * vertically above one x position, no series was clicked. See
 * {@link ChartPoint}.
 */
export function plotClick(spec: ChartSpec) {
  if (!spec.onPointClick) return undefined;
  const fire = spec.onPointClick;
  return (next: { activeTooltipIndex?: number | string | null } | undefined) => {
    const at = next?.activeTooltipIndex;
    // `Number(null)` is 0: a click off the plot would drill the first category.
    if (at === null || at === undefined || at === '') return;
    const category = typeof at === 'number' ? at : Number(at);
    if (!Number.isInteger(category) || category < 0) return;
    fire(
      // A single-series chart HAS an unambiguous series, and saying so lets a
      // drilldown from a one-line trend carry the same coordinate a bar would.
      (spec.series.length === 1 ? { category, series: 0 } : { category }) satisfies ChartPoint,
    );
  };
}

/**
 * Render a {@link ChartSpec} with Recharts.
 *
 * Matches the `ChartAdapter` signature, so it is provided as a value rather
 * than imported by the components that draw charts.
 */
export function rechartsAdapter(spec: ChartSpec) {
  return <RechartsChart spec={spec} />;
}

function RechartsChart({ spec }: { spec: ChartSpec }) {
  // Before the early return, because a hook cannot come after one.
  const animate = useMountAnimation();
  if (spec.kind === 'pie') return <PieAdapter spec={spec} animate={animate} />;

  const rows = toRows(spec);
  const showLegend = spec.series.length > 1;
  const clickable = spec.onPointClick !== undefined;

  // Bars on their side swap what each axis carries: the categories move to
  // the y-axis and the measure runs along the bottom. Everything else about
  // the chrome is identical, so the two orientations share one block rather
  // than forking into two nearly-identical charts.
  const sideways = spec.kind === 'bar' && spec.horizontal === true;
  const longest = sideways ? longestLabel(rows) : 0;

  /**
   * A ROW PER CATEGORY ONLY WHILE A ROW IS BIG ENOUGH TO READ.
   *
   * `interval={0}` is what stops Recharts thinning the labels, and it is the
   * point of the rotation — but it bounds nothing. Past `LABELLED_ROWS_MAX`
   * the rows are thinner than the text, so forcing every tick paints them on
   * top of one another: illegible, and worse than the thinning it replaced,
   * because nothing is legible rather than most things being.
   *
   * Past that count the axis goes back to the library's own thinning. Neither
   * answer is good at eighty categories — the honest fix is fewer categories,
   * a top-N with the tail folded into "Other" — but overlapping ink is the
   * one outcome that tells the reader nothing at all.
   */
  const labelEveryRow = spec.categories.length <= LABELLED_ROWS_MAX;

  // NO ROUNDED END WHEN ANY VALUE IS NEGATIVE. Recharts' `radius` is fixed in
  // the rect's own space, so on a bar drawn the other side of zero the rounded
  // corners land on the BASELINE and the square ones on the tip — backwards,
  // and the giveaway is a bar that looks detached from its axis. One negative
  // anywhere squares off the whole chart rather than rounding half of it
  // wrongly.
  const signed = spec.series.some((series) =>
    series.points.some((point) => typeof point === 'number' && point < 0),
  );

  // A sparkline draws the SHAPE and nothing else. Everything the axes would
  // say is already said by the number above it, in a box far too small to
  // read a tick in. The one thing it keeps is the HOVER: each point's period
  // and value, which the shape alone cannot tell a reader.
  const axes = spec.compact ? (
    sparkTooltip(spec)
  ) : (
    <>
      {/* Grid lines run PERPENDICULAR to the bars, so they cross the marks
          they are there to help measure. Upright bars want horizontal rules;
          sideways bars want vertical ones. */}
      <CartesianGrid
        stroke={GRID}
        strokeDasharray="3 3"
        vertical={sideways}
        horizontal={!sideways}
      />
      {sideways ? (
        <>
          <XAxis type="number" tick={AXIS} stroke={GRID} tickFormatter={spec.formatTick} />
          {/* `interval={0}` is the point of the rotation: EVERY category keeps
              its tick, where the default thins them to whatever fits — the
              silent label-dropping this orientation exists to stop. It holds
              up to `labelEveryRow`; see that binding for why it cannot hold
              past a certain density. `width` is measured off the longest
              label rather than fixed, so a chart of short names does not
              reserve a gutter it has no use for — capped, because past a
              point the labels would take the plot area they are labelling.
              Past the cap the tick truncates; see `categoryTick`, which also
              explains why it must never wrap. */}
          <YAxis
            type="category"
            dataKey="__label"
            tick={categoryTick(longest)}
            stroke={GRID}
            interval={labelEveryRow ? 0 : 'preserveStartEnd'}
            width={categoryGutter(longest)}
          />
        </>
      ) : (
        <>
          <XAxis dataKey="__label" tick={AXIS} stroke={GRID} interval="preserveStartEnd" />
          <YAxis tick={AXIS} stroke={GRID} tickFormatter={spec.formatTick} width={52} />
        </>
      )}
      <Tooltip
        formatter={tooltipFormatter(spec)}
        labelFormatter={(l) => `${spec.categoryLabel ? `${spec.categoryLabel}: ` : ''}${l}`}
        contentStyle={{
          background: 'var(--rk-surface, #fff)',
          border: `1px solid ${GRID}`,
          borderRadius: 6,
          fontSize: 12,
        }}
      />
      {showLegend && (
        <Legend formatter={(name) => spec.series[Number(String(name).slice(1))]?.label ?? name} />
      )}
    </>
  );

  // Edge to edge when compact: a sparkline inset by a chart's margins reads as
  // a chart that failed to fill its box.
  const margin = spec.compact
    ? { top: 2, right: 0, bottom: 0, left: 0 }
    : { top: 8, right: 8, bottom: 0, left: 0 };

  if (spec.kind === 'bar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={margin} layout={sideways ? 'vertical' : 'horizontal'}>
          {axes}
          {spec.series.map((s, si) => (
            <Bar
              key={s.key || si}
              dataKey={`s${si}`}
              // ONE stack id across every series is what stacking IS. Omitting
              // it groups them side by side, which is the unstacked chart.
              stackId={spec.stacked ? 'one' : undefined}
              fill={seriesFill(si)}
              maxBarSize={BAR_MAX_PX}
              // Round the END the value reaches and leave the baseline square,
              // so the bar still reads as growing from the axis. Not on a
              // STACK: there the far end of a segment is a join with the next
              // one, and rounding it puts a notch in the middle of a bar.
              radius={
                spec.stacked || signed ? undefined : sideways ? RADIUS_SIDEWAYS : RADIUS_UPRIGHT
              }
              isAnimationActive={animate}
              animationDuration={ANIMATION_MS}
              animationEasing="ease-out"
              onClick={barClick(spec, si)}
              cursor={clickable ? 'pointer' : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (spec.kind === 'area') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={rows}
          margin={margin}
          onClick={plotClick(spec)}
          style={clickable ? { cursor: 'pointer' } : undefined}
        >
          {axes}
          {spec.series.map((s, si) => (
            <Area
              key={s.key || si}
              dataKey={`s${si}`}
              stackId={spec.stacked ? 'one' : undefined}
              stroke={seriesColor(si)}
              fill={seriesColor(si)}
              fillOpacity={spec.stacked ? 0.85 : 0.25}
              // See the header: a gap in the data must stay a gap.
              connectNulls={false}
              isAnimationActive={animate}
              animationDuration={ANIMATION_MS}
              animationEasing="ease-out"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={rows}
        margin={margin}
        onClick={plotClick(spec)}
        style={clickable ? { cursor: 'pointer' } : undefined}
      >
        {axes}
        {spec.series.map((s, si) => (
          <Line
            key={s.key || si}
            dataKey={`s${si}`}
            stroke={seriesColor(si)}
            strokeWidth={2}
            // A point whose neighbours are both absent is a subpath with no
            // length. Without a dot it renders as nothing — correct and
            // invisible, which is worse than the zero it replaced.
            dot={spec.compact ? false : { r: 2 }}
            connectNulls={false}
            isAnimationActive={animate}
            animationDuration={ANIMATION_MS}
            animationEasing="ease-out"
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * A pie is the odd one out: no axes, and its slices are the CATEGORIES rather
 * than the series. {@link pivotToChartSpec} already normalised that into a
 * single series, so this reads `series[0]` and colours by category.
 */
function PieAdapter({ spec, animate }: { spec: ChartSpec; animate: boolean }) {
  const values = spec.series[0]?.points ?? [];
  const data = spec.categories
    .map((c, i) => ({ name: c.label, key: c.key, value: values[i], index: i }))
    // A pie's geometry cannot express a null or a negative, and drawing them
    // as zero-width slices leaves the legend claiming shares that are not
    // there. They are dropped from the drawing; the table still has them.
    .filter((d): d is typeof d & { value: number } => typeof d.value === 'number' && d.value > 0);

  if (data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="45%"
          outerRadius="75%"
          isAnimationActive={animate}
          animationDuration={ANIMATION_MS}
          animationEasing="ease-out"
          onClick={
            spec.onPointClick &&
            ((_: unknown, sliceIndex: number) => {
              // MAP BACK THROUGH THE DATUM. Recharts reports the index within
              // the array it was given, and that array was filtered — so on
              // any pie with a dropped slice the positional index is off by
              // however many came before it. The datum carries the index it
              // had in the spec for exactly this.
              const original = data[sliceIndex]?.index;
              if (typeof original === 'number') {
                spec.onPointClick?.({ category: original, series: 0 });
              }
            })
          }
          cursor={spec.onPointClick ? 'pointer' : undefined}
        >
          {data.map((d) => (
            <Cell key={d.key || d.index} fill={seriesFill(d.index)} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: unknown) => [
            spec.format(typeof value === 'number' ? value : null),
            spec.measureLabel,
          ]}
          contentStyle={{
            background: 'var(--rk-surface, #fff)',
            border: `1px solid ${GRID}`,
            borderRadius: 6,
            fontSize: 12,
          }}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}
